import { useId, useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { isEmailAddress } from '../deca/emailAddress.ts'
import { addAddresses, splitAddresses } from '../deca/emailRecipients.ts'
import { Icon } from './Icon.tsx'

/** The keys that end an address as it is typed: the separators the plan names, and Enter. */
const SEPARATORS = new Set([',', ';', ' ', 'Enter'])

/**
 * Email addresses as tags (#219): «Enviar DeCA a» on a DeCA, «Destinatarios del DeCA» on a book
 * entry, and the recipients reviewed before «Enviar».
 *
 * Typed or pasted, separated by commas, semicolons, spaces or line breaks: each becomes a tag. One
 * the server would not take stays, marked, for the form to refuse; a repeat is not added twice; past
 * [max] nothing more is added, and the field says what did not fit. An address that takes no mail
 * ([dead], from `useDeadAddresses`) is marked with why. Backspace on an empty box takes the last tag.
 */
export function EmailTagsInput({
  label,
  value,
  onChange,
  max,
  optional,
  help,
  error,
  dead,
  disabled,
}: {
  label: string
  value: readonly string[]
  onChange: (next: string[]) => void
  max?: number
  optional?: boolean
  help?: string
  error?: string
  /** Lower-case address → the reason no letter to it can arrive. */
  dead?: ReadonlyMap<string, string>
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const id = useId()
  const [text, setText] = useState('')
  const [overflow, setOverflow] = useState<string[]>([])

  const add = (raw: string) => {
    const incoming = splitAddresses(raw)
    if (incoming.length === 0) return
    const { next, overflow: left } = addAddresses(value, incoming, max)
    setOverflow(left)
    if (next.length !== value.length) onChange(next)
  }

  const commit = () => {
    add(text)
    setText('')
  }

  /**
   * The text as it changes: what stands before its last separator becomes tags, and what follows
   * stays to be typed. Read from the text, not only from the keys: a phone's keyboard sends no key
   * for a comma (Android reports «Unidentified»), and a browser fills a field without any.
   */
  const changeText = (next: string) => {
    const cut = Math.max(next.lastIndexOf(','), next.lastIndexOf(';'), next.search(/\s\S*$/))
    if (cut < 0) {
      setText(next)
      return
    }
    add(next.slice(0, cut))
    setText(next.slice(cut + 1).trimStart())
  }

  const remove = (address: string) => {
    setOverflow([])
    onChange(value.filter((a) => a !== address))
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (SEPARATORS.has(event.key)) {
      if (text.trim() === '' && event.key !== 'Enter') return
      event.preventDefault()
      commit()
      return
    }
    if (event.key === 'Backspace' && text === '' && value.length > 0) {
      event.preventDefault()
      remove(value[value.length - 1] as string)
    }
  }

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text')
    if (splitAddresses(pasted).length < 2 && !/[,;\s]/.test(pasted.trim())) return
    event.preventDefault()
    add(`${text} ${pasted}`)
    setText('')
  }

  const invalid = value.filter((address) => !isEmailAddress(address))
  const messages = [
    ...(invalid.length > 0 ? [t('emailTags.invalid')] : []),
    ...(overflow.length > 0 ? [t('emailTags.tooMany', { max, addresses: overflow.join(', ') })] : []),
    ...(error ? [error] : []),
  ]

  return (
    <div className="field field--full">
      <label className="field__label" htmlFor={id}>
        <span>
          {label}
          {optional ? <span className="field__optional"> ({t('common.optional')})</span> : null}
        </span>
      </label>
      <div className={`email-tags${messages.length > 0 ? ' email-tags--invalid' : ''}`}>
        {value.length > 0 ? (
          <ul className="email-tags__list" aria-label={label}>
            {value.map((address) => {
              const reason = dead?.get(address.toLowerCase())
              const bad = !isEmailAddress(address)
              const tone = bad ? 'danger' : reason ? 'warning' : 'neutral'
              return (
                <li key={address} className={`chip chip--${tone} email-tags__tag`} title={reason ? t(`emailTags.dead.${reason}`) : undefined}>
                  {bad || reason ? <Icon name="alert" size={14} /> : null}
                  <span>{address}</span>
                  {reason ? <span className="sr-only">{t(`emailTags.dead.${reason}`)}</span> : null}
                  {bad ? <span className="sr-only">{t('emailTags.invalidOne')}</span> : null}
                  <button
                    type="button"
                    className="email-tags__remove"
                    aria-label={t('emailTags.remove', { address })}
                    onClick={() => remove(address)}
                    disabled={disabled}
                  >
                    <Icon name="x" size={12} />
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}
        <input
          id={id}
          className="email-tags__input"
          type="text"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          value={text}
          placeholder={t('emailTags.placeholder')}
          disabled={disabled || (max !== undefined && value.length >= max)}
          aria-invalid={messages.length > 0}
          aria-describedby={`${id}-help`}
          onChange={(event) => changeText(event.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={commit}
        />
      </div>
      <span className="field__help" id={`${id}-help`}>
        {help ?? t('emailTags.help')}
      </span>
      {messages.map((message) => (
        <span key={message} className="field__error" role="status">
          <Icon name="alert" size={14} />
          {message}
        </span>
      ))}
    </div>
  )
}
